'use client';

import { useQuery } from '@tanstack/react-query';
import { roundToNearestMinutes } from 'date-fns';
import { type LiveResource, toLiveResource } from '@/data/liveResource';
import { config } from './config';

export interface Calendar {
  kind: string;
  etag: string;
  summary: string;
  updated: string;
  timeZone: string;
  accessRole: string; // 'reader'
  defaultReminders: { method: 'email' | 'popup'; minutes: number }[];
  nextSyncToken: string;
  items: CalendarEvent[];
}

export interface CalendarEvent {
  kind: string;
  etag: string;
  id: string;
  status: string; // 'confirmed'
  htmlLink: string;
  created: string;
  updated: string;
  summary: string;
  creator: { email: string };
  organizer: {
    email: string;
    displayName: string;
    self: boolean;
  };
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  recurrence?: string[];
  iCalUID: string;
  sequence: number;
  eventType: string; // 'default'
  description?: string;
  location?: string;
}

const CALENDAR_FETCH_ENABLED = false;

export const getGoogleCalendarUrl = () => {
  const currentDate = new Date();
  const calendarId = 'o7fuj1odginv2mhkmpibbinhv8@group.calendar.google.com';
  const accessKey = 'AIzaSyAuenQDlvNzDepv6kyNPoQ2SDLZ_ltLVfs';

  const timeMin = roundToNearestMinutes(currentDate, {
    nearestTo: 5,
  });
  const timeMax = new Date(timeMin.getTime() + 1000 * 60);

  return `https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events?key=${accessKey}&timeMin=${timeMin.toISOString()}&timeMax=${timeMax.toISOString()}`;
};

export const getCurrentCalendar = async () => {
  const calendar = (await fetch(getGoogleCalendarUrl()).then((response) =>
    response.json()
  )) as Calendar;

  const items = calendar.items || [];

  const currentEvent =
    items.find(({ summary }) => summary && summary.length > 0) || null;

  const currentLocation =
    items.find(({ location }) => location && location.length > 0)?.location ||
    'Barcelona, Spain';

  const unrestrictedGoogleGeocodingApiKey =
    'AIzaSyBJTxZgaY2ocPxnpG-qy6d5kOPPrY-Ei9g';

  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${currentLocation}&key=${unrestrictedGoogleGeocodingApiKey}`;
  const cachedResult = localStorage.getItem(`fetch:${url}`);

  const currentLocationLongLat = cachedResult
    ? JSON.parse(cachedResult)
    : currentLocation
      ? await fetch(url)
          .then((response) => response.json())
          .then(({ results }) => {
            const { location } = results[0].geometry;
            localStorage.setItem(`fetch:${url}`, JSON.stringify(location));
            return location;
          })
      : null;

  return {
    currentEvent,
    currentLocation: {
      title: currentLocation || 'Barcelona',
      long: currentLocationLongLat?.lng,
      lat: currentLocationLongLat?.lat,
    },
  };
};

type CalendarData = Awaited<ReturnType<typeof getCurrentCalendar>>;

export const useCalendar = (): LiveResource<CalendarData> => {
  const query = useQuery({
    queryKey: ['calendar'],
    queryFn: getCurrentCalendar,
    enabled: CALENDAR_FETCH_ENABLED,
    refetchInterval: config.refetchMiliseconds,
  });
  return toLiveResource(query);
};
